import { alpha, Box, Stack } from '@mui/material';
import { getShortString } from 'lib/functions/utils';
import React from 'react';

import { MobileBody, MobileSubtitle } from '../Typography';

export interface ContentCardHeaderProps {
  icon?: React.ReactNode;
  title: string;
  description?: string;
  clickable?: boolean;
  onClick?: () => void;
}

export type ContentCardHeaderColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning';

export interface ContentCardProps {
  children?: React.ReactNode;
  header?: ContentCardHeaderProps;
  headerColor?: ContentCardHeaderColor;
  headerWithBackground?: boolean;
  sx?: object;
}

const ContentCard: React.FC<ContentCardProps> = ({
  children,
  header,
  headerColor = 'primary',
  headerWithBackground = true,
  sx,
}) => {
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        overflow: 'hidden',
        boxShadow: 1,
        backgroundColor: 'background.paper',
        ...sx,
      }}
    >
      {header != null && (
        <Box
          component={header.clickable ? 'button' : 'div'}
          type={header.clickable ? 'button' : undefined}
          onClick={header.clickable ? header.onClick : undefined}
          sx={{
            ...(headerWithBackground
              ? {
                  background: (theme) => {
                    const color = theme.palette[headerColor].main;
                    return `linear-gradient(135deg, ${alpha(color, 0.12)} 0%, ${alpha(color, 0.05)} 50%, ${alpha(color, 0.01)} 100%)`;
                  },
                }
              : {}),
            borderRadius: 2.5,
            px: 1.5,
            py: 1.25,
            border: 'none',
            width: '100%',
            textAlign: 'left',
            backgroundColor: header.clickable ? 'transparent' : undefined,
            cursor: header.clickable ? 'pointer' : 'default',
            '&:hover': header.clickable
              ? {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.action.hover, 0.05),
                }
              : {},
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems={
              header.description != null && header.description !== ''
                ? 'flex-start'
                : 'center'
            }
          >
            {header.icon != null && (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  ...(headerWithBackground
                    ? {
                        backgroundColor: (theme) =>
                          alpha(theme.palette[headerColor].main, 0.2),
                      }
                    : {}),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: (theme) => theme.palette[headerColor].main,
                }}
              >
                {header.icon}
              </Box>
            )}
            <Box component="span" sx={{ minWidth: 0, display: 'block' }}>
              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <MobileSubtitle
                  component="span"
                  sx={{ color: 'text.primary', fontSize: '1rem' }}
                >
                  {getShortString(header.title, 25)}
                </MobileSubtitle>
                {header.description != null && header.description !== '' && (
                  <MobileBody
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontWeight: 400,
                    }}
                  >
                    {header.description}
                  </MobileBody>
                )}
              </Stack>
            </Box>
          </Stack>
        </Box>
      )}
      {children != null && <Box sx={{ p: 2 }}>{children}</Box>}
    </Box>
  );
};

export default ContentCard;
